import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import {
    FaShieldAlt,
    FaUsers,
    FaClock,
    FaAward,
    FaTools,
    FaHandshake,
    FaCheckCircle,
    FaArrowRight,
    FaPhoneAlt,
    FaMapMarkerAlt
} from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';

const WhyChooseUs = () => {
    const navigate = useNavigate();

    const [ref, inView] = useInView({
        threshold: 0.1,
        triggerOnce: true
    });

    const containerVariants = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: {
                staggerChildren: 0.15,
                duration: 0.6
            }
        }
    };

    const cardVariants = {
        hidden: { opacity: 0, y: 30 },
        visible: {
            opacity: 1,
            y: 0,
            transition: {
                duration: 0.6,
                ease: "easeOut"
            }
        }
    };

    // Navigation handler functions
    const handleQualityClick = () => {
        navigate('/about');
    };

    const handleContactClick = () => {
        navigate('/contact');
    };

    const handleExperienceClick = () => {
        navigate('/about');
    };

    const handleCatalogClick = () => {
        navigate('/products');
    };

    const handleServiceClick = () => {
        navigate('/service');
    };

    return (
        <section className="py-12 md:py-20 bg-white relative overflow-hidden" id="why-choose-us">
            <div className="container mx-auto px-4 md:px-8 relative z-10">
                {/* Header Section */}
                <motion.div
                    ref={ref}
                    initial="hidden"
                    animate={inView ? "visible" : "hidden"}
                    variants={containerVariants}
                    className="text-center mb-12 md:mb-16"
                >
                    <motion.div variants={cardVariants} className="inline-flex items-center gap-2 bg-secondary px-4 py-2 rounded-full mb-6">
                        <FaCheckCircle className="text-primary text-sm" />
                        <span className="text-primary font-semibold text-sm uppercase tracking-wider">
                            Why Choose AKTP
                        </span>
                    </motion.div>

                    <motion.h2
                        variants={cardVariants}
                        className="text-3xl md:text-4xl lg:text-5xl font-bold text-primary mb-6 leading-tight"
                    >
                        Industrial Solutions
                        <span className="block text-secondary">You Can Trust</span>
                    </motion.h2>
                </motion.div>

                {/* Cards Grid */}
                <motion.div
                    initial="hidden"
                    animate={inView ? "visible" : "hidden"}
                    variants={containerVariants}
                    className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8"
                >
                    {/* Quality Guarantee Card */}
                    <motion.div
                        variants={cardVariants}
                        whileHover={{ y: -5 }}
                        className="bg-primary text-white rounded-3xl p-8 relative overflow-hidden group cursor-pointer"
                        onClick={handleQualityClick}
                    >
                        {/* Background Image */}
                        <div className="absolute inset-0 opacity-10">
                            <img
                                src="/images/consultant.webp"
                                alt="Quality"
                                className="w-full h-full object-cover"
                            />
                        </div>

                        {/* Background Pattern */}
                        <div className="absolute top-0 right-0 w-24 h-24 bg-white/10 rounded-full -mr-12 -mt-12"></div>
                        <div className="absolute bottom-0 left-0 w-16 h-16 bg-white/5 rounded-full -ml-8 -mb-8"></div>

                        <div className="relative z-10">
                            <div className="w-16 h-16 bg-secondary rounded-2xl flex items-center justify-center mb-6">
                                <FaShieldAlt className="text-primary text-2xl" />
                            </div>

                            <div className="text-3xl font-bold text-secondary mb-2">100%</div>
                            <h3 className="text-xl font-bold mb-4">QUALITY GUARANTEED</h3>
                            <p className="text-gray-200 mb-6 leading-relaxed">
                                ISO certified products with manufacturer warranties and international standards compliance.
                            </p>

                            <div className="flex items-center text-secondary font-semibold group-hover:gap-3 transition-all duration-300">
                                <span>READ MORE</span>
                                <FaArrowRight className="ml-2 group-hover:translate-x-1 transition-transform duration-300" />
                            </div>
                        </div>
                    </motion.div>

                    {/* Working Hours Card - Updated */}
                    <motion.div
                        variants={cardVariants}
                        whileHover={{ y: -5 }}
                        className="bg-gradient-to-br from-orange-500 to-orange-600 text-white rounded-3xl p-8 relative overflow-hidden group cursor-pointer"
                        onClick={handleContactClick}
                    >
                        {/* Background Pattern */}
                        <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full -mr-16 -mt-16"></div>
                        <div className="absolute bottom-0 left-0 w-20 h-20 bg-white/5 rounded-full -ml-10 -mb-10"></div>

                        <div className="relative z-10">
                            <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center mb-6">
                                <FaClock className="text-white text-2xl" />
                            </div>

                            <h3 className="text-xl font-bold mb-6">Working Hours:</h3>

                            <div className="space-y-3 mb-8">
                                <div className="flex justify-between items-center">
                                    <span className="font-medium">Sat - Thu:</span>
                                    <span className="font-bold text-sm">8AM-1PM, 4PM-8:30PM</span>
                                </div>
                                <div className="flex justify-between items-center">
                                    <span className="font-medium">Friday:</span>
                                    <span className="font-bold">Closed</span>
                                </div>
                            </div>

                            <div className="flex items-center text-white font-semibold group-hover:gap-3 transition-all duration-300 border-b border-white/30 pb-1 inline-flex">
                                <span>CONTACT US</span>
                                <FaArrowRight className="ml-2 group-hover:translate-x-1 transition-transform duration-300" />
                            </div>
                        </div>
                    </motion.div>

                    {/* Experience Card - Updated to 20+ Years */}
                    <motion.div
                        variants={cardVariants}
                        whileHover={{ y: -5 }}
                        className="bg-white text-primary rounded-3xl p-8 relative overflow-hidden group cursor-pointer shadow-xl border border-gray-100"
                        onClick={handleExperienceClick}
                    >
                        {/* Background Pattern */}
                        <div className="absolute top-0 right-0 w-24 h-24 bg-secondary/10 rounded-full -mr-12 -mt-12"></div>
                        <div className="absolute bottom-0 left-0 w-16 h-16 bg-primary/5 rounded-full -ml-8 -mb-8"></div>

                        <div className="relative z-10">
                            <div className="w-16 h-16 bg-secondary/20 rounded-full flex items-center justify-center mb-6">
                                <FaAward className="text-secondary text-2xl" />
                            </div>

                            <div className="text-3xl font-bold text-secondary mb-2">20+</div>
                            <h3 className="text-xl font-bold mb-4">YEARS EXPERIENCE</h3>
                            <p className="text-gray-600 mb-6 leading-relaxed">
                                Two decades of industrial expertise with proven track record in electrical, hardware, and plumbing solutions.
                            </p>

                            <div className="flex items-center text-primary font-semibold group-hover:gap-3 transition-all duration-300">
                                <span>READ MORE</span>
                                <FaArrowRight className="ml-2 group-hover:translate-x-1 transition-transform duration-300" />
                            </div>
                        </div>
                    </motion.div>

                    {/* Products & Services Card */}
                    <motion.div
                        variants={cardVariants}
                        whileHover={{ y: -5 }}
                        className="bg-white text-primary rounded-3xl p-8 relative overflow-hidden group cursor-pointer shadow-xl border border-gray-100"
                        onClick={handleCatalogClick}
                    >
                        {/* Background Image */}
                        <div className="absolute inset-0 opacity-5">
                            <img
                                src="/images/electrician.webp"
                                alt="Industrial"
                                className="w-full h-full object-cover"
                            />
                        </div>

                        {/* Background Pattern */}
                        <div className="absolute top-0 right-0 w-28 h-28 bg-primary/5 rounded-full -mr-14 -mt-14"></div>

                        <div className="relative z-10">
                            <div className="w-16 h-16 bg-primary/10 rounded-2xl flex items-center justify-center mb-6">
                                <FaTools className="text-primary text-2xl" />
                            </div>

                            <div className="text-3xl font-bold text-secondary mb-2">5000+</div>
                            <h3 className="text-xl font-bold mb-4">PRODUCTS AVAILABLE</h3>
                            <p className="text-gray-600 mb-6 leading-relaxed">
                                Complete range of electrical components, hardware tools, plumbing materials, and explosion-proof equipment.
                            </p>

                            <div className="flex items-center text-primary font-semibold group-hover:gap-3 transition-all duration-300">
                                <span>VIEW CATALOG</span>
                                <FaArrowRight className="ml-2 group-hover:translate-x-1 transition-transform duration-300" />
                            </div>
                        </div>
                    </motion.div>
                </motion.div>

                {/* Bottom Stats Cards */}
                <motion.div
                    initial="hidden"
                    animate={inView ? "visible" : "hidden"}
                    variants={containerVariants}
                    className="grid grid-cols-2 md:grid-cols-3 gap-6 md:gap-8 mt-8"
                >
                    {/* Fast Delivery */}
                    <motion.div
                        variants={cardVariants}
                        whileHover={{ y: -3 }}
                        className="bg-gradient-to-r from-green-500 to-green-600 text-white rounded-2xl p-6 text-center group cursor-pointer"
                        onClick={handleServiceClick}
                    >
                        <FaClock className="text-3xl mb-3 mx-auto" />
                        <h4 className="font-bold text-lg mb-2">Fast Delivery</h4>
                        <p className="text-green-100 text-sm">Quick turnaround times</p>
                    </motion.div>

                    {/* Expert Support - Updated */}
                    <motion.div
                        variants={cardVariants}
                        whileHover={{ y: -3 }}
                        className="bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-2xl p-6 text-center group cursor-pointer"
                        onClick={handleServiceClick}
                    >
                        <FaUsers className="text-3xl mb-3 mx-auto" />
                        <h4 className="font-bold text-lg mb-2">Working Hours Support</h4>
                        <p className="text-blue-100 text-sm">Professional consultation</p>
                    </motion.div>

                    {/* Trusted Brands */}
                    <motion.div
                        variants={cardVariants}
                        whileHover={{ y: -3 }}
                        className="bg-gradient-to-r from-purple-500 to-purple-600 text-white rounded-2xl p-6 text-center group cursor-pointer col-span-2 md:col-span-1"
                        onClick={handleServiceClick}
                    >
                        <FaHandshake className="text-3xl mb-3 mx-auto" />
                        <h4 className="font-bold text-lg mb-2">20+ Brands</h4>
                        <p className="text-purple-100 text-sm">Authorized partnerships</p>
                    </motion.div>
                </motion.div>
            </div>
        </section>
    );
};

export default WhyChooseUs;